import Chart from 'chart.js/auto'
import moment from 'moment'

const RANGE_ID_1_MONTH = '1m'
const RANGE_ID_3_MONTHS = '3m'
const RANGE_ID_6_MONTHS = '6m'
const RANGE_ID_YEAR_TO_DATE = 'ytd'
const RANGE_ID_1_YEAR = '1y'
const RANGE_ID_5_YEARS = '5y'
const RANGE_ID_10_YEARS = '10y'

const capitalize = (text) => text.charAt(0).toUpperCase() +
    text.slice(1).toLowerCase()

const mediaQueryBreakpointDownSm = window.matchMedia(
    'only screen and (max-width: 760px)')

class FundDataSource {
    constructor (
        isin, lastNavValue, lastNavTrend, lastNavAum, lastNavDate, srri,
        esgRating, navSeries, downloads) {
        this.isin = isin
        this.lastNavValue = lastNavValue
        this.lastNavTrend = lastNavTrend
        this.lastNavAum = lastNavAum
        this.lastNavDate = lastNavDate
        this.srri = srri
        this.esgRating = esgRating
        this.navSeries = navSeries
        this.downloads = downloads
    }

    filterNavSeriesInRange (timeRange) {
        return this.navSeries.filter(
            navEntry => (navEntry.date.getTime() >=
                    timeRange.since.getTime()) &&
                (navEntry.date.getTime() <= timeRange.upto.getTime()))
    }

    generateRandomNavSeries (timeRage, min, max) {
        const SUNDAY = 0
        const SATURDAY = 6

        const navSeries = []
        for (let date = new Date(timeRage.upto.getTime()); date.getTime() >=
        timeRage.since.getTime(); date = new Date(
            date.setDate(date.getDate() - 1))) {

            if ([SATURDAY, SUNDAY].includes(date.getDay())) {
                continue
            }

            const value = Math.random() * (max - min) + min
            navSeries.push({
                date,
                value,
            })
        }

        return navSeries.reverse()
    }

    static fromData (fundData) {
        const graphRoot = (fundData || {})
        const lastNav = graphRoot.lastNav || {}
        const navSeries = graphRoot.navSeries || []

        const validSeriesEntry = entry => {
            const hasProps = (typeof entry === 'object') &&
                entry.hasOwnProperty('date') &&
                entry.hasOwnProperty('value') &&
                (typeof entry.value === 'number')

            if (!hasProps) {
                return false
            }

            const REGEXP_DATE = /^\d{4}-\d{2}-\d{2}$/

            return REGEXP_DATE.test((entry.date || ''))
        }

        const rejectedSize = 2
        const rejectedNavSeries = navSeries.filter(
            entry => !validSeriesEntry(entry))

        if (rejectedNavSeries.length > 0) {
            console.error(
                `NavSeries rejected #${rejectedNavSeries.length} top(${rejectedSize}): %o`,
                rejectedNavSeries.slice(0, rejectedSize))
        }

        navSeries.filter(validSeriesEntry).forEach(entry => {
            entry.date = new Date(entry.date)
        })

        const asDate = (dateString) => {
            const date = new Date(dateString)

            return date.constructor === Date ? date : undefined
        }

        return new FundDataSource(
            graphRoot.isin,
            lastNav.value,
            lastNav.trend,
            lastNav.aum,
            asDate(lastNav.date),
            graphRoot.srri,
            graphRoot.esgRating,
            navSeries,
            graphRoot.downloads || [],
        )
    }
}

class FundDataStore {
    constructor () {
        this.dataStore = {}
    }

    hasIsin (isin) {
        return this.dataStore.hasOwnProperty(isin) && this.dataStore[isin]
    }

    get (isin) {
        const deferredStore = $.Deferred()
        const fundDataUrl = `/wp-content/uploads/fund-portfolios/${isin}.json`

        if (!isin) {
            console.error('isin code is empty')
            return deferredStore.reject('isin code is empty')
        }

        if (this.hasIsin(isin)) {
            return deferredStore.resolve(this.dataStore[isin])
        }

        $.ajax({
            url: fundDataUrl,
            cache: false,
            method: 'get',
            dataType: 'json',
        }).then((fundsData, textStatus, jqXHR) => {
            const fundData = FundDataSource.fromData(fundsData)

            this.dataStore[isin] = fundData
            deferredStore.resolve(fundData)
        }, (jqXHR, textStatus, errorThrown) => {
            console.error(
                `Get fund data endpoint ${fundDataUrl} failure, reason "${textStatus}"`)
        })

        return deferredStore.promise()
    }
}

class Locale {
    static IT = 'it-IT'
    static EN = 'en-US'

    static isValid (locale) {
        return [this.IT, this.EN].includes(locale)
    }
}

class DateFormatter {
    constructor (locale) {
        this.locale = locale
    }

    format (date) {
        if (date.constructor !== Date) {
            return ''
        }

        return date.toLocaleDateString(this.locale)
    }

    formatMonth3Letters (date) {
        if (date.constructor !== Date) {
            return ''
        }

        return date.toLocaleDateString(this.locale, { month: 'short' })
    }
}

class NumberFormatter {
    constructor (locale) {
        this.locale = locale
    }

    formatEuro (number, includeSymbol = true) {
        return isNaN(number) ? '' : `${includeSymbol
            ? '€'
            : ''}${number.toLocaleString(this.locale,
            { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    }

    formatPercentage (number) {
        return isNaN(number) ? '' : `${number.toLocaleString(this.locale,
            { minimumFractionDigits: 3, maximumFractionDigits: 3 })}%`
    }

    formatNatural (number) {
        return isNaN(number) ? '' : `${number.toLocaleString(this.locale,
            { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    }
}

class NumberConverter {
    static UNIT_MIL = 'MIL'
    static UNIT_BIL = 'BIL'

    constructor (numberFormatter) {
        this.numberFormatter = numberFormatter
    }

    asEuroHumanReadable (number) {
        let unitEntries = [
            {
                multipleOf: Math.pow(10, 9),
                unit: NumberConverter.UNIT_BIL,
            }, {
                multipleOf: Math.pow(10, 6),
                unit: NumberConverter.UNIT_MIL,
            }]

        if (!isNaN(number)) {
            const unitEntry = unitEntries.find(
                (entry) => (number / entry.multipleOf) >= 1)

            if (unitEntry) {
                const numberFormatter = this.numberFormatter

                return {
                    numberInUnits: number / unitEntry.multipleOf,
                    unit: unitEntry.unit,
                    toString () {
                        return `${numberFormatter.formatEuro(
                            this.numberInUnits)}`
                    },
                }
            }
        }

        return {
            numberInUnits: number,
            unit: unitEntries,
            toString () {
                return `${this.numberInUnits} ${this.unit}`
            },
        }
    }
}

class TimeSpan {
    constructor () {
        const today = moment()
        today.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })

        this.today = today
    }

    [RANGE_ID_1_MONTH] () {
        return this.subMonths(1)
    }

    [RANGE_ID_3_MONTHS] () {
        return this.subMonths(3)
    }

    [RANGE_ID_6_MONTHS] () {
        return this.subMonths(6)
    }

    [RANGE_ID_YEAR_TO_DATE] () {
        const ytd = this.today.clone().startOf('year')

        return new Date(ytd.valueOf())
    }

    [RANGE_ID_1_YEAR] () {
        return this.subYears(1)
    }

    [RANGE_ID_5_YEARS] () {
        return this.subYears(5)
    }

    [RANGE_ID_10_YEARS] () {
        return this.subYears(10)
    }

    intervalFor (id) {
        const validRange = [
            RANGE_ID_1_MONTH,
            RANGE_ID_3_MONTHS,
            RANGE_ID_6_MONTHS,
            RANGE_ID_YEAR_TO_DATE,
            RANGE_ID_1_YEAR,
            RANGE_ID_5_YEARS,
            RANGE_ID_10_YEARS,
        ].includes(id)

        if (validRange) {
            if (typeof this[id] === 'function') {
                const since = this[id]()
                const upto = new Date(this.today.valueOf())

                return {
                    id,
                    since,
                    upto,
                }
            }
        }

        return undefined
    }

    subMonths (months) {
        const monthsAgo = this.today.clone().subtract(months, 'months')

        return new Date(monthsAgo.valueOf())
    }

    subYears (years) {
        const yearsAgo = this.today.clone().subtract(years, 'years')

        return new Date(yearsAgo.valueOf())
    }

    static defaultInterval () {
        const timeSpan = new TimeSpan()
        const id = RANGE_ID_1_MONTH
        const since = timeSpan[id]()
        const upto = timeSpan.today

        return {
            id,
            since,
            upto,
        }
    }
}

class MonthsLabelStrategy {
    constructor (dateFormatter, step) {
        this.dateFormatter = dateFormatter
        this.step = step || 1
    }

    generate (dates) {
        const asYearMonth = (date) => (date.getFullYear() * Math.pow(10, 4)) +
            date.getMonth()

        let current = undefined

        const lastMonthYear = dates.length > 0 ? asYearMonth(
            dates[dates.length - 1]) : undefined

        return dates.reverse().map((date, index) => {
            const yearMonth = asYearMonth(date)
            const multipleOfStep = !(index % this.step) || (this.step === 1)

            if ((index === 0) || (multipleOfStep &&
                ((current !== undefined) &&
                    (date.getFullYear() < current.year)))) {
                current = {
                    yearMonth,
                    year: date.getFullYear(),
                }

                return `${capitalize(this.dateFormatter.formatMonth3Letters(
                    date))} ${date.getFullYear()}.`
            }

            if ((multipleOfStep || (index === dates.length - 1)) &&
                (current !== undefined) &&
                (yearMonth < current.yearMonth) &&
                (yearMonth !== lastMonthYear)) {
                current = {
                    yearMonth,
                    year: date.getFullYear(),
                }

                return `${capitalize(
                    this.dateFormatter.formatMonth3Letters(date))}.`
            }

            return ''
        }).reverse()
    }
}

class YearsLabelStrategy {
    constructor (dateFormatter, step) {
        this.dateFormatter = dateFormatter
        this.step = step
    }

    generate (dates) {
        let current = undefined

        const lastYear = dates.length > 0
            ? dates[dates.length - 1].getFullYear()
            : undefined

        return dates.reverse().map((date, index) => {
            const year = date.getFullYear()
            const multipleOfStep = !(index % this.step) || (this.step === 1)

            if (index === 0) {
                current = year
                return `${capitalize(
                    this.dateFormatter.formatMonth3Letters(date))}. ${year}`
            }

            if ((multipleOfStep || (index === dates.length - 1)) &&
                (current !== null) && (year < current) &&
                (year !== lastYear)) {
                current = year

                return `${year}`
            }

            return ''
        }).reverse()
    }
}

class FundComponent {
    constructor (data, graph) {
        this.data = data
        this.graph = graph
        this.shouldRenderData = true
        this.canGenarateRandonNavSeries = false
    }

    render (timeRange) {
        let navSeries = this.data.fundDataSource.filterNavSeriesInRange(
            timeRange)

        if (this.shouldRenderData) {
            this.data.render()
            this.shouldRenderData = false
        }

        if (this.canGenarateRandonNavSeries && (navSeries.length === 0)) {
            navSeries = this.data.fundDataSource.generateRandomNavSeries(
                timeRange,
                300,
                1200)
        }

        let maxDataPointMap = {
            [RANGE_ID_10_YEARS]: 40,
            [RANGE_ID_5_YEARS]: 40,
            [RANGE_ID_1_YEAR]: 40,
            [RANGE_ID_YEAR_TO_DATE]: 40,
            [RANGE_ID_6_MONTHS]: 50,
            [RANGE_ID_3_MONTHS]: 50,
        }

        if (mediaQueryBreakpointDownSm.matches) {
            maxDataPointMap = {
                [RANGE_ID_10_YEARS]: 14,
                [RANGE_ID_5_YEARS]: 14,
                [RANGE_ID_1_YEAR]: 14,
                [RANGE_ID_YEAR_TO_DATE]: 14,
                [RANGE_ID_6_MONTHS]: 14,
                [RANGE_ID_3_MONTHS]: 14,
            }
        }

        if (maxDataPointMap.hasOwnProperty(timeRange.id)) {
            const dataStep = Math.ceil(
                navSeries.length / maxDataPointMap[timeRange.id])

            // FIXME uncomment below if u want to get only a discrete number of
            // values
            navSeries = navSeries.reverse().filter((value, index) => {
                return !(index % dataStep)
            }).reverse()
        }

        this.data.setNavPercentageIncrease(
            FundDataComponent.computeNavPercentageIncrease(navSeries))

        const labelStepMap = {
            [RANGE_ID_10_YEARS]: 10,
            [RANGE_ID_5_YEARS]: 6,
            [RANGE_ID_1_YEAR]: 12,
            [RANGE_ID_YEAR_TO_DATE]: 12,
            [RANGE_ID_6_MONTHS]: 5,
            [RANGE_ID_3_MONTHS]: 3,
            [RANGE_ID_1_MONTH]: 1,
        }

        const labelStepSize = labelStepMap.hasOwnProperty(timeRange.id)
            ? Math.ceil(navSeries.length / labelStepMap[timeRange.id])
            : 1

        const labelStrategy = [RANGE_ID_5_YEARS, RANGE_ID_10_YEARS].includes(
            timeRange.id)
            ? new YearsLabelStrategy(this.data.dateFormatter, labelStepSize)
            : new MonthsLabelStrategy(
                this.data.dateFormatter, labelStepSize)

        this.graph &&
        this.graph.render(
            navSeries,
            labelStrategy)
    }
}

class FundAccessor {
    constructor () {
        this.funds = {}
    }

    hasIsin (isin) {
        return this.funds.hasOwnProperty(isin) && this.funds[isin]
    }

    get (isin) {
        if (this.hasIsin(isin)) {
            return this.funds[isin]
        }

        return {
            render: (timeRage) => {},
        }
    }

    setFundForIsin (fundComposite, isin) {
        this.funds[isin] = fundComposite
    }
}

class DomUtils {
    static getSinglePortfolioDomNode () {
        return document.querySelector('[data-page=\'single-portfolio\']')
    }

    static getActiveFundTab () {
        return document.querySelector(
            '.single-portfolio .nav-tabs .nav-link.active')
    }

    static getActiveFundContent () {
        return document.querySelector(
            '.single-portfolio .tab-content .tab-pane.active')
    }

    static getAttributeFor (el, name) {
        return (el && el.getAttribute(name)) || undefined
    }

    static getIsinFor (el) {
        return this.getAttributeFor(el, 'data-isin')
    }

    static getActiveTimeRange () {
        const elActiveFundContent = DomUtils.getActiveFundContent()

        if (elActiveFundContent) {
            const timeSpan = new TimeSpan()
            const elActiveTimeSpan = elActiveFundContent.querySelector(
                '.time-bubbles>.time-range.selected')

            if (!elActiveTimeSpan) {
                return undefined
            }

            const rangeId = elActiveTimeSpan.getAttribute('data-range')

            return timeSpan.intervalFor(rangeId)
        }
    }

    static renderFund (isin) {
        const elFundContent = DomUtils.getActiveFundContent()
        const timeRage = DomUtils.getActiveTimeRange() ||
            TimeSpan.defaultInterval()

        const elFundGraph = elFundContent.querySelector(
            '[data-ref=\'fund-portfolios-graph-canvas\']')

        fundDataStore.get(isin).
            done((fundData, textStatus, jqXHR) => {
                if (!fundAccessor.hasIsin(isin)) {
                    const fundDataComponent = new FundDataComponent(fundData,
                        elFundContent, numberFormatter,
                        dateFormatter)

                    let fundGraphComponent = elFundGraph ?
                        new FundGraphComponent(elFundGraph,
                            numberFormatter,
                            dateFormatter) : undefined

                    const fund = new FundComponent(fundDataComponent,
                        fundGraphComponent)
                    fundAccessor.setFundForIsin(fund, isin)

                    fund.render(timeRage)

                    return
                }

                fundAccessor.get(isin).render(timeRage)
            })
    }

    static bindEvents () {
        var elFundsTabs = document.querySelectorAll(
            '.single-portfolio .nav-link[data-bs-toggle="tab"]')

        for (const elFundsTab of elFundsTabs) {
            elFundsTab.addEventListener('shown.bs.tab', function (event) {
                const currentTab = event.target
                const isin = DomUtils.getIsinFor(currentTab)

                DomUtils.renderFund(isin)
            })

            const fundTabId = (elFundsTab.getAttribute('href') || '').replace(
                /^#/,
                '')

            if (fundTabId) {
                const elFundContent = document.querySelector(
                    `.single-portfolio #${fundTabId}.tab-pane`)

                const elTimeRanges = elFundContent.querySelectorAll(
                    '.time-bubbles>.time-range')

                for (const elTimeRange of elTimeRanges) {
                    elTimeRange.addEventListener('click', (event) => {
                        const currentTab = DomUtils.getActiveFundTab()
                        const isin = DomUtils.getIsinFor(currentTab)
                        const elTimeRange = event.target

                        elTimeRanges.forEach(
                            (elTimeRange) => {
                                elTimeRange.classList.remove('selected')
                            })
                        elTimeRange.classList.add('selected')

                        DomUtils.renderFund(isin)
                    })
                }
            }
        }
    }

    static getLocale () {
        return this.getAttributeFor(document.querySelector('html'), 'lang')
    }
}

function createNumberFormatter () {
    const locale = DomUtils.getLocale()

    if (Locale.isValid(locale)) {
        return new NumberFormatter(locale)
    }

    console.error(`Number formatter, locale $(locale) not supported`)

    return {
        formatEuro: (number, includeSymbol = true) => undefined,
        formatPercentage: (number) => undefined,
        formatNatural: (number) => undefined,
    }
}

function createDateFormatter () {
    const locale = DomUtils.getLocale()

    if (Locale.isValid(locale)) {
        return new DateFormatter(Locale.IT)
    }

    console.error(`Date formatter, locale $(locale) not supported`)

    return {
        format: (date) => undefined,
        formatMonth3Letters: (date) => undefined,
    }
}

class FundDataComponent {
    constructor (dataSource, dataSheet, numberFormatter, dateFormatter) {
        this.fundDataSource = dataSource
        this.fundDataSheet = dataSheet
        this.numberFormatter = numberFormatter
        this.dateFormatter = dateFormatter
    }

    setIsin (text) {
        const el = this.fundDataSheet.querySelector(
            '[data-ref=\'isin\']')
        el.textContent = text
    }

    setLastNavValue (number) {
        const el = this.fundDataSheet.querySelector(
            '[data-ref=\'lastNav_value\']')
        el.textContent = this.numberFormatter.formatEuro(number)
    }

    setLastNavTrend (number) {
        this.setPercentageFor(this.fundDataSheet.querySelector(
            '[data-ref=\'lastNav_trend\']'), number)
    }

    setLastNavAum (number) {
        const el = this.fundDataSheet.querySelector(
            '[data-ref=\'lastNav_aum\']')

        try {
            const numberConverter = new NumberConverter(this.numberFormatter)
            const numberForHumans = numberConverter.asEuroHumanReadable(number)
            const labelForUnit = DomUtils.getAttributeFor(el,
                `data-unit-${(numberForHumans.unit || '').toLowerCase()}`) || ''

            el.textContent = `${numberForHumans.toString()} ${labelForUnit}`
        }
        catch (err) {
            console.log(err)
        }

    }

    setLastNavDate (dateString) {
        const el = this.fundDataSheet.querySelector(
            '[data-ref=\'lastNav_date\']')

        el.textContent = this.dateFormatter.format(dateString)
    }

    setSsri (number) {
        const el = this.fundDataSheet.querySelector(
            '[data-ref=\'srri\']')

        el.textContent = this.numberFormatter.formatNatural(number)
    }

    setEsgRating (number) {
        const elUnit = this.fundDataSheet.querySelector(
            '[data-ref=\'esgRating_unit\']')

        const elItems = this.fundDataSheet.querySelector(
            '[data-ref=\'esgRating_items\']')

        elItems.innerHTML = ''

        if (number > 0) {
            for (let leafIndex = 0; leafIndex < number; ++leafIndex) {
                const elLeave = elUnit.cloneNode(true)
                elLeave.classList.remove('d-none')
                elLeave.removeAttribute('data-ref')
                elItems.appendChild(elLeave)
            }
        }
    }

    setNavPercentageIncrease (number) {
        this.setPercentageFor(this.fundDataSheet.querySelector(
            '[data-ref=\'nav__percentageIncrease\']'), number)
    }

    setDownloads (downloads) {
        const breakpoints = ['lg', 'md']
        const documentTypes = ['KIIDOC', 'CPROSP', 'SEMREP', 'ANNREP']
        const locales = ['it', 'en']

        const findDownload = (documentType) => downloads.find(
            entry => documentType === entry.documentType)
        const italianSite = DomUtils.getLocale() === Locale.IT

        for (const breakpoint of breakpoints) {
            for (const documentType of documentTypes) {
                const elDownloadEntry = this.fundDataSheet.querySelector(
                    `[data-ref=\'downloads_${breakpoint}_${documentType}\']`)

                if (!elDownloadEntry) {
                    continue
                }

                let shouldDisplayDownload = false

                for (const locale of locales) {
                    const items = (findDownload(documentType) || {}).items || []
                    const url = (items.find(entry => entry.locale === locale) ||
                        {}).url

                    const elDownloadLocale = elDownloadEntry.querySelector(
                        `[data-ref=\'${locale}\']`)
                    if ((!italianSite && locale !== 'it') || italianSite) {
                        url && elDownloadLocale.classList.remove('download-hidden')
                        elDownloadLocale.href = url || '#'
                    }

                    shouldDisplayDownload = shouldDisplayDownload || !!url
                }

                if (shouldDisplayDownload) {
                    elDownloadEntry.classList.remove('d-none')
                }
            }
        }
    }

    setPercentageFor (el, number) {
        if (!isNaN(number)) {
            el.classList.remove('text-danger', 'text-success')
            el.classList.add(number >= 0 ? 'text-success' : 'text-danger')
        }

        el.textContent = `${number >= 0
            ? '+'
            : ''}${this.numberFormatter.formatPercentage(
            number)}`
    }

    static computeNavPercentageIncrease (navSeries) {
        const { 0: previousNav, [navSeries.length - 1]: currentNav } = navSeries

        if (previousNav && previousNav.value && currentNav &&
            currentNav.value) {
            return ((currentNav.value - previousNav.value) /
                previousNav.value) * 100
        }

        return undefined
    }

    render () {
        this.setIsin(this.fundDataSource.isin)
        this.setLastNavValue(this.fundDataSource.lastNavValue)
        this.setLastNavTrend(this.fundDataSource.lastNavTrend)
        this.setLastNavAum(this.fundDataSource.lastNavAum)
        this.setLastNavDate(this.fundDataSource.lastNavDate)
        this.setSsri(this.fundDataSource.srri)
        this.setEsgRating(this.fundDataSource.esgRating)
        this.setDownloads(this.fundDataSource.downloads)
    }
}

class FundGraphComponent {
    static DATASET_FUND = 0
    static POINT_RADIUS_FUND = 8

    constructor (canvas, numberFormatter, dateFormatter) {
        this.canvas = canvas
        this.numberFormatter = numberFormatter
        this.dateFormatter = dateFormatter
        this.chart = undefined
    }

    setUpChartGlobals () {
        Chart.defaults.color = '#a6abd3'
        Chart.defaults.font.family = '"Sohne",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans","Liberation Sans",sans-serif'
        Chart.defaults.font.size = 11
    }

    createConfig (context) {
        const gradient = context.createLinearGradient(0, 0, 0, 450)
        gradient.addColorStop(0.1, 'rgba(0, 106, 255, 1)')
        gradient.addColorStop(0.7, 'rgba(27, 30, 59, 1)')
        gradient.addColorStop(0.8, 'rgba(27, 30, 59, 0.6)')

        const numberFormatter = this.numberFormatter

        return {
            type: 'line',
            data: {
                labels: [],
                datasets: [
                    {
                        data: [],
                        backgroundColor: gradient,
                        borderColor: '#00b4fa',
                        fill: true,
                        pointBackgroundColor: '#ffd144',
                        pointBorderColor: '#ffd144',
                        pointHoverRadius: FundGraphComponent.POINT_RADIUS_FUND,
                        pointRadius: [],
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                parsing: {
                    xAxisKey: 'date',
                    yAxisKey: 'value',
                },
                interaction: {
                    intersect: false,
                    axis: 'x',
                },
                scales: {
                    x: {
                        display: true,
                        grid: {
                            borderColor: '#a6abd3',
                            display: false,
                        },
                        ticks: {
                            callback: function (value) {
                                return this.getLabelForValue(value) || undefined
                            },
                            align: 'end',
                        },
                    },
                    y: {
                        display: true,
                        grid: {
                            display: false,
                        },
                        beginAtZero: false,
                        ticks: {
                            callback: function (value) {
                                return numberFormatter.formatEuro(value, false)
                            },
                            maxTicksLimit: 5,
                        },
                    },
                },
                plugins: {
                    title: {
                        display: false,
                    },
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        enabled: true,
                        callbacks: {
                            title: (contextMap) => {
                                context = contextMap[FundGraphComponent.DATASET_FUND]
                                const labelOrig = context.label ||
                                    ''
                                const dataForIndex = context.dataset.data[context.dataIndex]
                                const labelAlt = dataForIndex &&
                                dataForIndex.date &&
                                dataForIndex.date.constructor === Date
                                    ? this.dateFormatter.format(
                                        dataForIndex.date)
                                    : undefined

                                return labelAlt || labelOrig
                            },
                        },
                    },
                },
            },
        }
    }

    updateConfig (config, data, labelStrategy) {
        const dataSetFund = config.data.datasets[FundGraphComponent.DATASET_FUND]
        const datesOnly = data.map(entry => entry.date)
        const labels = labelStrategy.generate(datesOnly)

        const pointRadiusZeroPadding = '#'.repeat(
            Math.max(labels.length - 1, 0)).
            split('').
            map(_ => 2)

        config.data.labels = labels

        const values = data.map(entry => entry.value)
        const min = Math.min(...values)
        const max = Math.max(...values)
        const upperBoundPercentage = 5
        const lowerBoundPercentage = 45
        const percentageOf = (partial, total) => ((total / 100) * partial)

        config.options.scales.y.min = !isNaN(min)
            ? Math.max(0,
                Math.ceil(min - percentageOf(lowerBoundPercentage, min)))
            : undefined

        config.options.scales.y.max = !isNaN(max)
            ? Math.ceil(max + percentageOf(upperBoundPercentage, max))
            : undefined

        dataSetFund.data = data
        dataSetFund.pointRadius = [
            ...pointRadiusZeroPadding,
            FundGraphComponent.POINT_RADIUS_FUND]
    }

    render (dataSeries, labelStrategy) {
        if (!this.chart) {
            const context = this.canvas.getContext('2d')
            const config = this.createConfig(context)

            this.setUpChartGlobals()
            this.updateConfig(config, dataSeries, labelStrategy)

            this.chart = new Chart(this.canvas, config)

            return
        }

        this.updateConfig(this.chart, dataSeries, labelStrategy)

        this.chart.update()
    }
}

const fundAccessor = new FundAccessor()
const fundDataStore = new FundDataStore()
const numberFormatter = createNumberFormatter()
const dateFormatter = createDateFormatter()

export function initPortfolioFunds () {
    DomUtils.bindEvents()

    const isin = DomUtils.getIsinFor(DomUtils.getActiveFundTab())

    DomUtils.renderFund(isin)
}